import {
    AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, Optional, Output,
    ViewChild
} from '@angular/core';
import {fromEvent, merge } from 'rxjs';
import {takeUntil, switchMap, pairwise, map} from 'rxjs/operators'
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {NbinputConfig, SystemAttachment} from './nbinput.entity';
import {NbinputUploadService} from './nbinput-upload';



@Component({
    selector: 'nbinput-sign',
    template: require('./nbinput-sign.component.html'),
    styles: [require('./nbinput-sign.component.scss')],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NbinputSignComponent),
            multi: true
        }
    ]
})

export class NbinputSignComponent implements AfterViewInit, ControlValueAccessor {
    @Input()
    config: NbinputConfig = new NbinputConfig();

    private onTouchedCallback = () => {};
    private onChangeCallback = (_) => {};

    writeValue(val: SystemAttachment): void {
        // 获取服务器已有的签名作为背景
        this.bgImage = val ? (this.config.downloadAPI ? this.config.downloadAPI + val.url : val.url) : null;
        this.initBackground(this.canvas.nativeElement.width, this.canvas.nativeElement.height);
    }
    registerOnChange(fn: any): void {if (fn) { this.onChangeCallback = fn; }}
    registerOnTouched(fn: any): void {if (fn) { this.onTouchedCallback = fn; }}
    setDisabledState?(isDisabled: boolean): void {this.disabled = isDisabled; }

    scaleRatio(canvasWidth: number, canvasHeight: number, imageWidth: number, imageHeight: number) {
        if (imageWidth / canvasWidth > imageHeight / canvasHeight) {
            return {
                width: canvasWidth,
                height: canvasWidth * imageHeight / imageWidth,
            };
        } else {
            return {
                width: canvasHeight * imageWidth / imageHeight,
                height: canvasHeight,
            };
        }
    }

    public bgImage: string;

    @Input()
    set data(val) {
        this.bgImage = val ? (this.config.downloadAPI ? this.config.downloadAPI + val.url : val.url) : null;
        this.initBackground(this.canvas.nativeElement.width, this.canvas.nativeElement.height);
    }

    @ViewChild('canvas') public canvas: ElementRef;
    @ViewChild('back') public background: ElementRef;

    @HostListener('window:resize', ['$event.target'])
    onResize() {
        this.canvas.nativeElement.width = this.canvas.nativeElement.offsetWidth;
        this.canvas.nativeElement.height = this.canvas.nativeElement.offsetHeight;
        this.background.nativeElement.width = this.background.nativeElement.offsetWidth;
        this.background.nativeElement.height = this.background.nativeElement.offsetHeight;
        this.cx.lineWidth = 2;
        this.cx.lineCap = 'round';
        this.cx.strokeStyle = '#f00';
        this.initBackground(this.background.nativeElement.width, this.background.nativeElement.height);
    }

    public disabled;
    @Input()
    public readonly = false;

    public isDraw = false;
    public editable = false;
    switchEditable() {
        if (!this.readonly && !this.disabled) {
            if (this.editable && this.isDraw) {
                // const dataURL = this.canvas.nativeElement.toDataURL("image/png");
                const dataURL = this.getTrimedImage();
                const blobBin = atob(dataURL.split(',')[1]);
                const array = [];
                for (let i = 0; i < blobBin.length; i++) {
                    array.push(blobBin.charCodeAt(i));
                }
                const res = new Blob([new Uint8Array(array)], {type: 'image/png'});
                this.uploader.upload(
                    this.uploader.blobToFile(res, 'sign' + (new Date().getUTCMilliseconds()) + '.png'),
                    this.config.uploadAPI)
                    .subscribe(e => {
                        this.submit(e);
                    });
            }
            this.editable = !this.editable;
        }
    }

    getTrimedImage() {
        const copy = document.createElement('canvas').getContext('2d');
        const pixels = this.cx.getImageData(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
        const pdl = pixels.data.length;
        let i;
        const bound = {
            top: null,
            left: null,
            right: null,
            bottom: null
        };
        let x, y;
        for (i = 0; i < pdl; i += 4) {
            if (pixels.data[i + 3] !== 0) {
                x = (i / 4) % this.canvas.nativeElement.width;
                y = ~~((i / 4) / this.canvas.nativeElement.width);
                if (bound.top === null) {
                    bound.top = y;
                }
                if (bound.left === null) {
                    bound.left = x;
                } else if (x < bound.left) {
                    bound.left = x;
                }
                if (bound.right === null) {
                    bound.right = x;
                } else if (bound.right < x) {
                    bound.right = x;
                }
                if (bound.bottom === null) {
                    bound.bottom = y;
                } else if (bound.bottom < y) {
                    bound.bottom = y;
                }
            }
        }
        const trimHeight = bound.bottom - bound.top,
            trimWidth = bound.right - bound.left,
            trimmed = this.cx.getImageData(bound.left, bound.top, trimWidth, trimHeight);
        copy.canvas.width = trimWidth;
        copy.canvas.height = trimHeight;
        copy.putImageData(trimmed, 0, 0);

        // open new window with trimmed image:
        return copy.canvas.toDataURL('image/png')
    }

    @Output()
    public onSubmit: EventEmitter<any> = new EventEmitter();
    submit(val: SystemAttachment) {
        this.onChangeCallback(val);
        this.onSubmit.emit(val);
    }

    private cx: CanvasRenderingContext2D;
    private bg: CanvasRenderingContext2D;

    public ngAfterViewInit() {
        const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;
        this.cx = canvasEl.getContext('2d');
        const bgEl: HTMLCanvasElement = this.background.nativeElement;
        this.bg = bgEl.getContext('2d');
        this.onResize();
        this.captureEvents(this.canvas.nativeElement);
    }

    private captureEvents(canvasEl: HTMLCanvasElement) {
        fromEvent(canvasEl, 'mousedown')
            .pipe(
                map((e: Event) => {
                    if (!this.readonly && !this.disabled && this.editable) {
                        e.preventDefault();
                        return e;
                    }
                }),
                switchMap(() => fromEvent(canvasEl, 'mousemove')
                    .pipe(
                        map((e: Event) => {
                            if (!this.readonly && !this.disabled && this.editable) {
                                e.preventDefault();
                                return e;
                            }
                        }),
                        takeUntil(
                            merge(
                                fromEvent(canvasEl, 'mouseup'),
                                fromEvent(canvasEl, 'mouseout')
                            )
                        ),
                        pairwise()
                    )
                )
            )
            .subscribe((res: [MouseEvent, MouseEvent]) => {
                if (!this.readonly && !this.disabled && this.editable) {
                    const rect = canvasEl.getBoundingClientRect();
                    const prevPos = {
                        x: res[0].clientX - rect.left,
                        y: res[0].clientY - rect.top
                    };
                    const currentPos = {
                        x: res[1].clientX - rect.left,
                        y: res[1].clientY - rect.top
                    };
                    this.drawOnCanvas(prevPos, currentPos);
                }
            });
        fromEvent(canvasEl, 'touchstart')
            .pipe(
                map((e: Event) => {
                    if (!this.readonly && !this.disabled && this.editable) {
                        e.preventDefault();
                        return e;
                    }
                }),
                switchMap(() =>
                    merge(
                        fromEvent(canvasEl, 'touchmove'),
                        fromEvent(window, 'touchmove')
                    ).pipe(
                        map((e: Event) => {
                            if (!this.readonly && !this.disabled && this.editable) {
                                e.preventDefault();
                                return e;
                            }
                        }),
                        takeUntil(fromEvent(canvasEl, 'touchend')),
                        pairwise()
                    )
                )
            )
            .subscribe((res: [TouchEvent, TouchEvent]) => {
                if (!this.readonly && !this.disabled && this.editable) {
                    const rect = canvasEl.getBoundingClientRect();
                    const prevPos = {
                        x: res[0].changedTouches[0].clientX - rect.left,
                        y: res[0].changedTouches[0].clientY - rect.top
                    };
                    const currentPos = {
                        x: res[1].changedTouches[0].clientX - rect.left,
                        y: res[1].changedTouches[0].clientY - rect.top
                    };
                    this.drawOnCanvas(prevPos, currentPos);
                }
            });
    }

    private drawOnCanvas(
        prevPos: { x: number, y: number },
        currentPos: { x: number, y: number }
    ) {
        if (!this.cx) { return; }
        this.cx.beginPath();
        if (prevPos) {
            this.cx.moveTo(prevPos.x, prevPos.y);
            this.cx.lineTo(currentPos.x, currentPos.y);
            this.cx.stroke();
            this.isDraw = true;
        }
    }

    initBackground(w: number, h: number) {
        if (this.bgImage) {
            const img = new Image();
            img.addEventListener('load', () => {
                const dim = this.scaleRatio(w, h, img.width, img.height);
                this.cx.drawImage(img, (w - dim.width) / 2, (h - dim.height) / 2, dim.width, dim.height); }, false);
            img.src = this.bgImage;
        } else if (this.bg) {
            this.bg.font = '64px serif';
            this.bg.textAlign = 'center';
            this.bg.textBaseline = 'middle';
            this.bg.strokeStyle = '#ddd';
            this.bg.strokeText('签名处', w / 2, h / 2);
        }
    }

    public clearCanvas() {
        this.cx.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
        this.isDraw = false;
        this.initBackground(this.canvas.nativeElement.width, this.canvas.nativeElement.height);
    }

    @Input()
    type;

    @ViewChild('fileInput') fileInput: ElementRef;
    uploadImage(e) {
        if (e.target.files.length > 0) {
            this.bgImage = URL.createObjectURL(e.target.files[0]);
            this.uploader.upload(e.target.files[0], this.config.uploadAPI)
                .subscribe(f => {
                    this.submit(f);
                });
            this.initBackground(this.canvas.nativeElement.width, this.canvas.nativeElement.height);
        }
        // console.log(e.target.files);
    }
    chooseImage(e) {
        e.stopPropagation();
        this.fileInput.nativeElement.click();
    }

    constructor(
        private uploader: NbinputUploadService,
    ) {
    }
}
